import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Group,
  Popover,
  Text,
  Badge,
  Tooltip,
  Stack,
} from '@mantine/core';
import { LuAtom } from 'react-icons/lu';
import { useItemEntryContext } from '../../../../../entry-editor/context';
import { EntryItemType, EntryType, TreeNode } from '@morph-mapper/types';
import { useStore } from '@/store';
import type { StoredSuggestion, Suggestion, SuggestionFound } from '@/store';
import { useSuggestionRunner } from '@/hooks';
import { useEntryContext } from '../../../../../context-editor/context';

const NO_SUGGESTIONS: StoredSuggestion[] = [];
const BUSY = new Set(['queued', 'running']);

/** Marks the suggestions the last re-evaluation of the field found. */
const FoundBadge = ({ found }: { found?: SuggestionFound }) => {
  if (found !== 'latest') return null;
  return (
    <Badge size="xs" color="green" variant="light">
      new
    </Badge>
  );
};

const useEntrySuggestions = (entryId = '') => {
  const suggestions = useStore((s) => s.ai.suggestions[entryId]);
  const status = useStore((s) => s.ai.fieldStatus[entryId]);
  const markSuggestionApplied = useStore(({ ai }) => ai.markSuggestionApplied);

  return {
    suggestions: suggestions ?? NO_SUGGESTIONS,
    loading: BUSY.has(status?.status),
    error: status?.error,
    markApplied: (suggestion: Suggestion) => {
      if (entryId) markSuggestionApplied(entryId, suggestion);
    },
  };
};

export const AiSuggestions = ({
  mode = 'auto' as 'auto' | 'row' | 'item',
  iterator = false,
}) => {
  if (iterator) {
    return <IteratorAiSuggestions />;
  } else {
    return <FieldAiSuggestions mode={mode} />;
  }
};

const FieldAiSuggestions = ({ mode = 'auto' as 'auto' | 'row' | 'item' }) => {
  let entryNode: TreeNode | undefined;
  let setEditing: (v: boolean) => void;
  let setType: (t: EntryItemType) => void;
  let currentType: EntryType;
  let setTempValue: (v: any) => void;
  let setTemplateValue: (v: any) => void;
  let hasValue = false;

  try {
    const itemVals = useItemEntryContext((s: any) => [
      s.getById?.(),
      s.setEditing,
      s.setType,
      s.getType?.(),
      s.setNewValue ?? s.setTempValue,
      s.setTemplateValue,
      s.hasValue?.(),
    ]);
    entryNode = itemVals[0] as TreeNode;
    setEditing = itemVals[1] as any;
    setType = itemVals[2] as any;
    currentType = itemVals[3] as any;
    setTempValue = itemVals[4] as any;
    setTemplateValue = itemVals[5] as any;
    hasValue = (itemVals[6] as boolean) ?? false;
  } catch {
    console.error('failed to get the entryNode');
    currentType = EntryType.Simple;
    hasValue = false;
  }

  const { evaluateField } = useSuggestionRunner();
  const textContent = useStore((s) => s.config.textContent);
  const entryId = (entryNode as any)?.id;

  const [opened, setOpened] = useState(false);
  const { suggestions, markApplied, loading, error } =
    useEntrySuggestions(entryId);

  const bootstrapVersion = useStore((s) => s.ai.bootstrapVersion);
  const setUserInteracted = useStore((s) => s.ai.setUserInteracted);
  const addAppliedKey = useStore((s) => s.ai.addAppliedKey);
  const resetForNewFile = useStore((s) => s.ai.resetForNewFile);

  const formatValue = (value: unknown) => {
    try {
      if (typeof value === 'string') return value;
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const open = () => {
    setOpened(true);
    void fetchSuggestions();
  };

  const fetchSuggestions = async (): Promise<Suggestion[]> =>
    entryId ? evaluateField(entryId) : [];

  const isApplyingRef = useRef(false);
  const apply = (s: Suggestion) => {
    try {
      isApplyingRef.current = true;
      if (currentType !== s.type) {
        setType(s.type as unknown as EntryItemType);
      }
      if (s.type === EntryType.Code) {
        if (typeof setTemplateValue === 'function') {
          setTemplateValue(s.snippet);
        } else {
          setTempValue(s.snippet);
        }
      } else {
        if (typeof setTemplateValue === 'function') {
          setTemplateValue(s.value);
        } else {
          setTempValue(s.value);
        }
      }
      markApplied(s);
      setEditing(false);
      setOpened(false);
    } catch (e) {
      console.error('[AI] Failed to apply suggestion:', e);
    } finally {
      isApplyingRef.current = false;
    }
  };

  // Track whether we have auto-applied for the current loaded file content (local to this instance)
  const appliedForContentRef = useRef<unknown>(null);
  const lastTextContentRef = useRef<unknown>(textContent);
  const lastTypeRef = useRef<EntryType | null>(null);

  // Track user interactions to prevent auto-apply during manual changes
  useEffect(() => {
    const handleUserInteraction = () => {
      setUserInteracted(true);
    };

    document.addEventListener('click', handleUserInteraction);
    return () => {
      document.removeEventListener('click', handleUserInteraction);
    };
  }, [setUserInteracted]);

  // Detect genuine file changes vs component remounts
  useEffect(() => {
    const ai = useStore.getState().ai;
    if (textContent !== ai.lastTextContent) {
      // Genuine file change — reset guards
      appliedForContentRef.current = null;
      lastTextContentRef.current = textContent;
      resetForNewFile(textContent as string | null);
    } else {
      // Same file, just a remount — keep guards intact
      lastTextContentRef.current = textContent;
    }
  }, [textContent, resetForNewFile]);

  // Mark user interaction when entry type changes to avoid re-applying suggestions
  useEffect(() => {
    if (lastTypeRef.current === null) {
      lastTypeRef.current = currentType;
      return;
    }
    if (currentType !== lastTypeRef.current) {
      if (isApplyingRef.current) {
        lastTypeRef.current = currentType;
        return;
      }
      setUserInteracted(true);
      appliedForContentRef.current = textContent;
      lastTypeRef.current = currentType;
    }
  }, [currentType, textContent, setUserInteracted]);

  // Auto-apply effect
  useEffect(() => {
    const ai = useStore.getState().ai;

    if (mode !== 'auto') return;
    if (!textContent) return;
    if (hasValue) return;
    if (ai.userInteracted) return;
    if (appliedForContentRef.current === textContent) return;

    void (async () => {
      const key = `${entryId ?? 'unknown'}::${String(textContent)}`;
      if (ai.hasAppliedKey(key)) {
        appliedForContentRef.current = textContent;
        return;
      }
      addAppliedKey(key);
      const list = await fetchSuggestions();
      if (list && list.length > 0) {
        const autoSuggestion = list.find((s) => s.autofill === true);

        if (autoSuggestion) {
          apply(autoSuggestion);
          appliedForContentRef.current = textContent;
        }
      }
    })();
  }, [hasValue, mode, textContent, bootstrapVersion, addAppliedKey]);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      withinPortal
      trapFocus
      position="bottom-end"
    >
      <Popover.Target>
        <Tooltip label="AI suggestions">
          <Box
            onClick={open}
            sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: theme.radius.sm,
              border: `1px solid ${theme.colors.gray[4]}`,
              backgroundColor: theme.white,
              color: theme.colors.dark[7],
              cursor: 'pointer',
              fontSize: 12,
              lineHeight: 1,
              userSelect: 'none',
            })}
            title="AI"
            aria-label="AI suggestions"
            role="button"
            tabIndex={0}
          >
            <LuAtom size={14} />
          </Box>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Box sx={{ minWidth: 320 }}>
          <Group position="apart" mb="xs">
            <Text weight={600}>AI Suggestions</Text>
            <Badge color="blue" variant="light">
              {loading ? 'Loading' : `${suggestions.length}`}
            </Badge>
          </Group>
          {error && (
            <Text color="red" size="sm" mb="xs">
              {error}
            </Text>
          )}
          {!loading && suggestions.length === 0 && !error ? (
            <Text size="sm" color="dimmed">
              No suggestions available
            </Text>
          ) : null}
          {loading ? (
            <Text size="sm">Fetching suggestions...</Text>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {suggestions.map((s, idx) => (
                <Group key={idx} position="apart" spacing="xs">
                  <Tooltip label={s.source} position="left">
                    <Box
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 220,
                      }}
                    >
                      <Text size="sm">{formatValue(s.value)}</Text>
                    </Box>
                  </Tooltip>
                  <Group spacing="xs">
                    <FoundBadge found={s.found} />
                    <Badge variant="outline">{`${s.type}`}</Badge>
                    <Button size="xs" onClick={() => apply(s)}>
                      Apply
                    </Button>
                  </Group>
                </Group>
              ))}
            </Box>
          )}
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

const IteratorAiSuggestions = () => {
  const { evaluateField } = useSuggestionRunner();

  // Suggestions belong to the map's #iterator entry, not to the map.
  const [entryId, fillSuggestion] = useEntryContext((s) => [
    s.getIteratorId(),
    s.fillSuggestion,
  ]);

  const [opened, setOpened] = useState(false);
  const { suggestions, markApplied, loading, error } =
    useEntrySuggestions(entryId);

  const fetchSuggestions = async (): Promise<Suggestion[]> =>
    entryId ? evaluateField(entryId) : [];

  const open = () => {
    setOpened(true);
    void fetchSuggestions();
  };

  const apply = (s: Suggestion) => {
    try {
      fillSuggestion(s.snippet);
      markApplied(s);
      setOpened(false);
    } catch (e) {
      console.error('[AI][Iterator] Failed to apply suggestion:', e);
    }
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      withinPortal
      position="bottom-end"
    >
      <Popover.Target>
        <Tooltip label="AI iterator suggestions">
          <Box
            onClick={open}
            sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: theme.radius.sm,
              border: `1px solid ${theme.colors.gray[4]}`,
              backgroundColor: theme.white,
              color: theme.colors.dark[7],
              cursor: 'pointer',
              fontSize: 12,
              lineHeight: 1,
              userSelect: 'none',
            })}
            role="button"
            tabIndex={0}
          >
            <LuAtom size={14} />
          </Box>
        </Tooltip>
      </Popover.Target>

      <Popover.Dropdown>
        <Box sx={{ minWidth: 320 }}>
          <Group position="apart" mb="xs">
            <Text weight={600}>AI Suggestions</Text>
            <Badge variant="light">
              {loading ? 'Loading' : suggestions.length}
            </Badge>
          </Group>

          {error && (
            <Text color="red" size="sm" mb="xs">
              {error}
            </Text>
          )}

          {!loading && suggestions.length === 0 && !error && (
            <Text size="sm" color="dimmed">
              No suggestions available
            </Text>
          )}

          {loading ? (
            <Text size="sm">Fetching suggestions...</Text>
          ) : (
            <Stack spacing="xs">
              {suggestions.map((s, idx) => (
                <Group key={idx} position="apart">
                  <Tooltip label={s.source} position="left">
                    <Text size="sm" lineClamp={1}>
                      {s.value ?? s.type}
                    </Text>
                  </Tooltip>
                  <Group spacing="xs">
                    <FoundBadge found={s.found} />
                    <Badge variant="outline">{s.type}</Badge>
                    <Button size="xs" onClick={() => apply(s)}>
                      Apply
                    </Button>
                  </Group>
                </Group>
              ))}
            </Stack>
          )}
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};
